import { useId, useState } from "react";
import { Box, Button, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";

import DataTable from "./DataTable.jsx";
import EmptyState from "./EmptyState.jsx";

/**
 * The one sentence every chart carries about what it counts (LO-218).
 *
 * Not a footer on a page of charts: it is the basis behind these numbers, so it
 * sits under the figure it qualifies and is part of what a screen reader says
 * about it. Two people looking at the same saved report can see different
 * totals, and a total with no sentence is a total somebody will quote as if it
 * were everyone's.
 *
 * It says what is counted and that a different person counts differently. It
 * does not say that anything is excluded, how much, or whether anything is: a
 * figure that hinted at hidden records would be telling the reader that they
 * exist, which is the one thing the access rule withholds.
 */
export const CHART_BASIS =
  "Counts only the records you can open, so a person with different access "
  + "sees different numbers.";

const SLOTS = ["s1", "s2", "s3", "s4"];

/**
 * A series' colour, by what the chart holds and never by what a value is.
 *
 * One series takes the brand colour. Several take the four categorical slots
 * in fixed order, and any after the fourth fold to the neutral `other`
 * rather than a fifth hue nobody could tell apart (see `chart` in `theme.js`).
 */
export function colourFor(index, count) {
  if (count <= 1) return "single";
  return index < SLOTS.length ? SLOTS[index] : "other";
}

/**
 * A number in the chart's own units. Money arrives in minor units, as stored.
 *
 * `compact` is for an axis: `$80K`, not `8,000,000`, which an axis has no room
 * for and which, in minor units, would also be wrong by a factor of a hundred.
 */
export function formatter({ style = "number", currency, minorUnits = false, compact = false } = {}) {
  const notation = compact ? { notation: "compact", maximumFractionDigits: 1, minimumFractionDigits: 0 } : {};
  if (style === "money" && currency) {
    const probe = new Intl.NumberFormat(undefined, { style: "currency", currency });
    const digits = probe.resolvedOptions().maximumFractionDigits;
    const divisor = minorUnits ? 10 ** digits : 1;
    const money = new Intl.NumberFormat(undefined, { style: "currency", currency, ...notation });
    return (value) => (value == null ? "" : money.format(value / divisor));
  }
  const plain = new Intl.NumberFormat(undefined, notation);
  return (value) => (value == null ? "" : plain.format(value));
}

function when(asOf) {
  const at = new Date(asOf);
  if (Number.isNaN(at.getTime())) return null;
  return at;
}

/**
 * A chart over a query result, with what it counts said beside it.
 *
 * ```jsx
 * <Chart
 *   title="Open cases by week"
 *   kind="bar"
 *   dimension={{ key: "week", label: "Week" }}
 *   series={[{ key: "cases", label: "Open cases" }]}   // a series may carry its own `valueFormat`
 *   rows={[{ week: "2026-10-05", cases: 4 }, ...]}
 *   asOf="2026-10-21T03:00:00Z"
 * />
 * ```
 *
 * ## What it will not draw
 *
 * - **A chart with nothing to say what it counts.** `CHART_BASIS` is not a
 *   prop and cannot be switched off, for the same reason `DataTable`'s scroll
 *   container cannot: a caller who wants it gone will have a reason on the
 *   day, and the figure is wrong without it.
 * - **A fifth hue.** See `colourFor`.
 * - **A dimension the kernel flagged.** The query service refuses a group-by
 *   on a `no_group_by` field and this never sees one, so there is no check
 *   here to disagree with it.
 *
 * ## Colour is never the only signal
 *
 * Series are named in a legend, in the tooltip and in the table, and the
 * table is always one button away: it is also the way to read the chart
 * without seeing it, and the relief the data-viz method asks for where two of
 * the four hues sit under 3:1 on the light surface.
 *
 * ## Why this imports a chart library when `Sparkline` does not
 *
 * `Sparkline` is fifty lines of SVG because it draws one shape and refuses
 * anything it cannot label. A report has axes, several series, a legend,
 * tooltips and keyboard reach, and writing those again would make the kit's
 * charts the kit's own argument. `@mui/x-charts` is an optional peer, so an
 * app that draws no chart ships none of it; import this component from its own
 * path, never from the package root.
 *
 * ## Opening what is behind a mark
 *
 * `onOpen({ index, row, seriesKey, series })` is called when a bar or a point
 * is clicked, when Enter is pressed on a focused one, and when a figure in the
 * table is pressed. The table is the way a person who cannot see or aim at the
 * mark does the same thing. The chart only says *which* row and series; what
 * to do about it, and whether the person may, is the caller's and the
 * kernel's. Left out, nothing is clickable and the caption does not mention it.
 *
 * ## What the as-of time is
 *
 * When the copy the numbers came from was last brought up to date, as the
 * query service reports it. It is not when the chart was drawn.
 */
export default function Chart({
  title,
  kind = "bar",
  dimension,
  series,
  rows,
  valueFormat,
  asOf,
  unlinked,
  onOpen,
  height = 280,
  sx,
}) {
  const theme = useTheme();
  const titleId = useId();
  const basisId = useId();
  const tableId = useId();
  const [table, setTable] = useState(false);

  const list = rows || [];
  // A series may carry its own format: money split by currency is one series
  // per currency, and each is shown in its own.
  const formats = series.map((s) => formatter(s.valueFormat || valueFormat));
  const palette = theme.vars?.palette?.chart || theme.palette.chart;
  const colours = series.map((_, i) => palette[colourFor(i, series.length)]);
  const stamp = asOf ? when(asOf) : null;

  const chartSeries = series.map((s, i) => ({
    id: s.key,
    dataKey: s.key,
    label: s.label,
    color: colours[i],
    valueFormatter: (v) => formats[i](v),
    // A weekly count has no value between the weeks, so the line goes straight
    // from point to point and each point is drawn. A smooth curve would show
    // the reader a middle that nobody measured.
    ...(kind === "line" ? { curve: "linear", showMark: true } : {}),
  }));

  // Counts and sums start at zero. An axis that starts at the lowest value makes
  // a rise of two look like a doubling, so the floor is zero unless a value is
  // negative, when the library is left to choose.
  const nonNegative = list.every((row) =>
    series.every((s) => !(Number(row[s.key]) < 0)));
  // The axis is read in the first series' own units, compactly. With several
  // currencies it is the first one's symbol on a scale that is each one's own
  // major units, which is why a chart of mixed currencies is a table instead.
  const axisFormat = formatter({ ...(series[0]?.valueFormat || valueFormat), compact: true });
  const yAxis = [{ ...(nonNegative ? { min: 0 } : {}), valueFormatter: (v) => axisFormat(v) }];
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  const xAxis = [{
    scaleType: "band",
    // `data` and a formatter rather than `dataKey` alone: the sentence a screen
    // reader speaks for a mark is built from the axis' own data and formatter,
    // and from `dataKey` it came out as the series and the value with no week.
    data: list.map((row) => row[dimension.key]),
    valueFormatter: (value) => String(value ?? ""),
    label: dimension.label,
    // Explicit, because with an axis `label` and no height @mui/x-charts 9.16
    // sizes the axis itself and draws every tick label empty. Found in a real
    // browser: jsdom has no layout and showed nothing wrong.
    height: 56,
  }];

  const Plot = kind === "line" ? LineChart : BarChart;
  const width = Math.max(list.length * 36, 0);

  // A mark, a click or Enter on a focused one, and a cell of the table all say
  // the same thing: which row, and which series. The caller decides what that
  // is worth; this never reads a record.
  const open = onOpen
    ? (index, seriesKey) => {
      if (typeof index === "number" && index >= 0 && index < list.length) {
        onOpen({ index, row: list[index], seriesKey, series: series.find((s) => s.key === seriesKey) });
      }
    }
    : undefined;
  const plotHandlers = !open ? {} : kind === "line"
    ? { onMarkClick: (_, id) => open(id?.dataIndex, String(id?.seriesId)) }
    : { onItemClick: (_, id) => open(id?.dataIndex, String(id?.seriesId)) };

  return (
    <Box
      component="figure"
      aria-labelledby={titleId}
      aria-describedby={basisId}
      sx={{ m: 0, minWidth: 0, ...sx }}
    >
      <Typography
        id={titleId}
        variant="subtitle1"
        component="figcaption"
        sx={{ fontWeight: 600, mb: 1 }}
      >
        {title}
      </Typography>

      {list.length ? (
        // Wide content scrolls in its own container, never the page.
        <Box sx={{ overflowX: "auto", maxWidth: "100%" }}>
          <Box sx={{ minWidth: width }}>
            <Plot
              dataset={list}
              xAxis={xAxis}
              yAxis={yAxis}
              series={chartSeries}
              skipAnimation={reduceMotion}
              height={height}
              hideLegend={series.length < 2}
              slotProps={{ legend: { sx: { flexWrap: "wrap" } } }}
              // Enter on a focused mark is the same as a click on it.
              experimentalFeatures={open ? { keyboardActivation: true } : undefined}
              {...plotHandlers}
            />
          </Box>
        </Box>
      ) : (
        <EmptyState title="Nothing to draw">
          <Typography variant="body2">
            No records you can open match this chart. That does not mean none
            exist.
          </Typography>
        </EmptyState>
      )}

      <Typography
        id={basisId}
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1 }}
      >
        {CHART_BASIS}
        {typeof unlinked === "number" && unlinked > 0 ? (
          <>
            {" "}
            {unlinked.toLocaleString()}{" "}
            {unlinked === 1 ? "record has" : "records have"} no accepted link
            between sources and {unlinked === 1 ? "is" : "are"} not in this
            chart.
          </>
        ) : null}
        {stamp ? (
          <>
            {" "}
            As of <time dateTime={stamp.toISOString()}>{stamp.toLocaleString()}</time>.
          </>
        ) : null}
        {open && list.length ? (
          <>
            {" "}
            Select a {kind === "line" ? "point" : "bar"}, or a figure in the table, to see the records behind it.
          </>
        ) : null}
      </Typography>

      {list.length ? (
        <Stack sx={{ mt: 1, alignItems: "flex-start", gap: 1 }}>
          <Button
            size="small"
            onClick={() => setTable((open) => !open)}
            aria-expanded={table}
            aria-controls={tableId}
          >
            {table ? "Hide the table" : "Show as a table"}
          </Button>
          {table ? (
            <Box id={tableId} sx={{ width: "100%", minWidth: 0 }}>
              <DataTable
                columns={[
                  { key: dimension.key, label: dimension.label },
                  ...series.map((s, i) => ({
                    key: s.key,
                    label: s.label,
                    align: "right",
                    render: open
                      ? (row) => (
                        <Button
                          size="small"
                          sx={{ minWidth: 0, fontVariantNumeric: "tabular-nums" }}
                          onClick={() => open(list.indexOf(row), s.key)}
                          aria-label={`${formats[i](row[s.key])}: show the records behind ${dimension.label} ${row[dimension.key]}${series.length > 1 ? `, ${s.label}` : ""}`}
                        >
                          {formats[i](row[s.key])}
                        </Button>
                      )
                      : (row) => formats[i](row[s.key]),
                  })),
                ]}
                rows={list}
              />
            </Box>
          ) : null}
        </Stack>
      ) : null}
    </Box>
  );
}
